import { Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { AdaptiveDpr } from '@react-three/drei';
import { CelestialProvider } from './celestial/CelestialProvider.jsx';
import { FlatScene } from './FlatScene.jsx';
import { JourneyScene } from './JourneyScene.jsx';
import { PostFX } from './PostFX.jsx';
import { Starfield } from './celestial/Starfield.jsx';

// End-of-frame alpha-fix pass (Safari speck fix). The canvas context is
// transparent+premultiplied, and the postFX composer's final pass leaves the
// framebuffer alpha at 0 while colors carry energy — spec-UNDEFINED
// premultiplied data that Safari's compositor renders as green/magenta
// specks over bright glows (WebKit bug 278926; Chromium clamps it away).
// Writing alpha=1 across the canvas after ALL rendering makes the buffer
// valid/opaque for every compositor. Runs at priority 2 — after the
// composer's render at priority 1 — and touches only the alpha channel.
// MOUNT ONLY WITH POSTFX: a priority>0 useFrame disables R3F's auto-render,
// which the composer replaces on tier ≥2; on lower tiers there is no
// composer to replace it (this alone would black-screen them) and no fix
// needed — three's background clear already writes alpha=1 there.
function AlphaFix() {
  useFrame(({ gl }) => {
    const ctx = gl.getContext();
    ctx.colorMask(false, false, false, true);
    ctx.clearColor(0, 0, 0, 1);
    ctx.clear(ctx.COLOR_BUFFER_BIT);
    ctx.colorMask(true, true, true, true);
  }, 2);
  return null;
}

export function SpaceCanvas({ perfTier, mode = 'flat' }) {
  return (
    <Canvas
      className="space-canvas"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        // journey needs raycast hover on the ring tablets; DOM tiles sit above
        // the canvas and still receive their own events first
        pointerEvents: mode === 'journey' ? 'auto' : 'none',
      }}
      frameloop="always"
      dpr={perfTier.dpr}
      camera={{ position: [0, 0, 8], fov: 45 }}
      // alpha:false documents intent (no page show-through) and keeps three's
      // own non-composer clear path opaque — but three r185 creates the
      // CONTEXT with alpha regardless, so the actual Safari-speck fix is the
      // <AlphaFix /> end-of-frame pass below (see its comment).
      gl={{ antialias: false, powerPreference: 'high-performance', alpha: false }}
    >
      <color attach="background" args={['#050608']} />
      <fog attach="fog" args={['#050608', 30, 120]} />
      <ambientLight intensity={0.24} />
      <directionalLight position={[6, 2.5, 5]} intensity={1.8} color="#fff4e2" />
      <AdaptiveDpr pixelated />
      <CelestialProvider perfTier={perfTier} visibleGroups={[]}>
        <Starfield />
        <Suspense fallback={null}>{mode === 'journey' ? <JourneyScene /> : <FlatScene />}</Suspense>
      </CelestialProvider>
      <PostFX perfTier={perfTier} />
      {perfTier.postFX && <AlphaFix />}
    </Canvas>
  );
}
